import * as React from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { League, Player } from '../../../../sdk/model';
import { styled } from '@mui/material/styles';
import { Box, Dialog, DialogTitle, IconButton, DialogContent, Typography, 
    DialogActions, InputLabel, Select, MenuItem, Button, Grid, Modal, Card, CardContent } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import Close from "@mui/icons-material/Close";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { contractApi, teamApi } from '../../../../adapters/APIExporter';
import PlayerCardLink from '../../../cards/playerCardLink';
import TeamRosterCustom from '../../team/roster/TeamRosterCustom';
import { useLoadLeagueData } from '../../../../app/dataLoaderHooks';
import TeamDisplay from '../../team/TeamDisplay';
import CustomBidModal from '../../auction/component/CustomBidModal';
import { MINIMUM_ONE_YEAR_SALARY } from '../../../util/ContractUtils';

interface manageRostersProps {
  league: League
}

function ManageRosters(props: manageRostersProps) {
  const [file, setFile] = React.useState<File | undefined>(undefined);
  const [seasonYear, setSeasonYear] = React.useState<number>(props.league.currentSeasonYear!);
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<Array<string>>([]);
  const [selectedTeamIndex, setSelectedTeamIndex] = React.useState(0);
  const [playerToAdd, setPlayerToAdd] = React.useState<Player | undefined>(undefined);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  
  const user = useAppSelector((state) => state.user);
  
  const league : League|undefined = useLoadLeagueData({
      leagueId: props.league.id, 
      userId: user.id, 
      loadBasicDraftData: false,
      loadFullDraftData: false, 
      loadContractData: false,
      loadBasicAuctionData: false, 
      loadFullAuctionData: false,
      loadRosterData: true, 
      loadScheduleData: false, 
      loadWaivedContracts: false,
      isException, 
      setIsException, 
      isLoadComplete, 
      setLoadComplete
    });
  
  const playerDataLoad = useAppSelector((state) => state.playerDataLoad );
  
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });
  
  function uploadContracts() {
    setErrors([]);
    setSuccess(false);
    contractApi.importContracts(props.league.id, seasonYear, file).then(response => {
      setFile(undefined);
      setSuccess(true);
    }).catch(error => {
      setErrors([error.response.data.message]);
    }).finally(() => {
      setUploading(false);
    })
  }
  
  let errorInfo = errors.map((error, index) => {
    return (<p className='error' key={'error_' + index}>{error}</p>);
  });
  
  const unavailablePlayers = new Set(league?.teams.flatMap(team => {
      const playerRoster = team.playerRoster ? team.playerRoster : [];
      return Object.values(playerRoster).flat().filter(player => player).map(player => player!.playerId);
    }));
    
  const playerMap = new Map(playerDataLoad.flatMap(team => team.players).map((player) => [player.id, player]));
    
  const freeAgents = playerDataLoad.flatMap(team => team.players).filter(player => !unavailablePlayers.has(player?.id));
  
  const salaryCap = props.league?.currentSeason?.nflSeason?.salaryCap === undefined ? 0 : props.league?.currentSeason?.nflSeason?.salaryCap;
  const selectedTeam = league?.teams[selectedTeamIndex];
  
  const freeAgentColumns = React.useMemo(() => [{
    accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''} ${row.abbr_name || ''}`.trim(),
    id: 'player',
    header: 'Player',
    minSize: 80,
    size: 80,
    enableGlobalFilter: true,
    Cell: ({ row }) => (
      <PlayerCardLink player={row.original} leagueId={props.league.id!} />
    )
  }], [])
  
  function addPlayer(playerId, teamId, totalAmount, years) {
    teamApi.addPlayerToTeam(playerId, teamId, totalAmount, years).then(() => {
      setPlayerToAdd(undefined);
    });
  }
  
  if (!isLoadComplete) {
    return <Typography>Loading...</Typography>
  }
  
  return <Box>
    <Dialog open={uploading} maxWidth="sm" fullWidth>
      <DialogTitle>Import Contracts?</DialogTitle>
      <Box position="absolute" top={0} right={0}>
        <IconButton onClick={() => setUploading(false)}>
          <Close />
        </IconButton>
      </Box>
      <DialogContent>
        <Typography>Are you sure you want to replace all contracts in your league with the contracts in this CSV?  This action is irreversable!</Typography>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={() => setUploading(false)}>
          Cancel
        </Button>
        <Button color="secondary" variant="contained" onClick={uploadContracts}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
    <Modal open={playerToAdd !== undefined} onClose={() => setPlayerToAdd(undefined)} 
        aria-labelledby="Add Player Modal" aria-describedby="Add player to selected team"
        sx={{margin: '5%'}}>
      <Card style={{maxHeight: 800, overflow: 'auto'}}><CardContent>
        <Typography>Add <PlayerCardLink player={playerToAdd} leagueId={props.league.id!} /> to <TeamDisplay leagueId={props.league.id!} team={selectedTeam} emphasized={false} teamId={selectedTeam.id!} /></Typography>
        <CustomBidModal minimumOneYearOfferValue={MINIMUM_ONE_YEAR_SALARY} currentSeason={props.league.currentSeasonYear} maxBid={salaryCap - (selectedTeam.currentSeasonSalary === undefined ? 0 : selectedTeam.currentSeasonSalary)} disabled={false} auctionId={undefined} teamId={selectedTeam.id} remainingContractLengths={new Map([[2,1],[3,1],[4,1]])} selectedPlayer={playerToAdd} save={addPlayer} />
      </CardContent></Card>
    </Modal>
    <Button
        className='capsized-button'
        component="label"
        role={undefined}
        variant="contained"
        tabIndex={-1}
        startIcon={<CloudUploadIcon />}>
      Upload Contract CSV
      <VisuallyHiddenInput type="file" onChange={event => setFile(event.target.files![0])} />
    </Button>
    <br/>
    {file !== undefined && file.name}
    <br/>
    <InputLabel id="seasonYearLabel">Contract Terms Apply to:</InputLabel>
    <Select name="seasonYear" label="Contract Terms Effective" labelId="seasonYearLabel" value={seasonYear}
        onChange={event => setSeasonYear(event.target.value!)}>
      <MenuItem value={props.league.currentSeasonYear! - 1}>{props.league.currentSeasonYear! - 1}</MenuItem>
      <MenuItem value={props.league.currentSeasonYear!}>{props.league.currentSeasonYear!}</MenuItem>
    </Select>
    <br/>
    <Button className='capsized-button' disabled={!file} onClick={() => setUploading(true)}>Import Contracts into League</Button>
    {success && <p className='success'>Import successful!</p>}
    {errorInfo}
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <MaterialReactTable 
            enableMultiRowSelection={false}
            getRowId={(row) => row.id || ''}
            columns={freeAgentColumns} 
            data={freeAgents} 
            enableRowActions
            enableGlobalFilter={true}
            
            renderRowActions={({ row }) => {
              return <Button className="capsized-button" onClick={() => setPlayerToAdd(row.original)}>Add player to selected team</Button>
            }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Select name="team" label="Team" labelId="teamLabel" value={selectedTeamIndex}
            onChange={event => setSelectedTeamIndex(Number(event.target.value))}>
          {league!.teams.map((team, teamIndex) => 
            <MenuItem key={teamIndex} value={teamIndex}>{league!.teams[teamIndex].name}</MenuItem>
          )}
        </Select>
        <TeamRosterCustom 
            leagueId={props.league.id!} 
            team={league!.teams[selectedTeamIndex]} 
            currentSeasonYear={props.league.currentSeasonYear!}
            playerMap={playerMap} 
            rosterSettings={new Map(Object.entries(props.league.rosterSettings || {})) as Map<string, number>} 
            myTeam={true} 
            commish={true}
        />
      </Grid>
    </Grid>
  </Box>
}

export default ManageRosters;
